import { useState } from 'react'
import { useFormContext, FieldErrors, FieldValues } from 'react-hook-form'
import { IFormConfig } from 'types'
import { useTranslation } from 'react-i18next'
import {
  IndividualType,
  IndividualWithRelations,
  IndividualRelationObject,
  IndividualContactType,
  useCreateIndividualWithRelationMutation,
  useGetMerchantApplicationQuery,
  useUpdateIndividualMutation,
  useUpdateIndividualRelationMutation,
  useRemoveIndividualMutation
} from 'generated/graphql'
import {
  MainContact,
  getMainContactModalConfig,
  ChargebackContact,
  getChargebackContactModalConfig,
  Signer,
  getSignerModalConfig
} from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs'
import { findByRole, mapSignerToInput, mapToFormIndividual } from 'utils'
import { IndividualWithRelationsFormValues } from 'pages/Onboarding'

export type ModalState =
  | { open: false }
  | {
      open: true
      role: 'mainContact'
      mode: 'edit'
      id: string
      index: number
      defaultValues: MainContact
    }
  | { open: true; role: 'chargebackContact'; mode: 'add' }
  | {
      open: true
      role: 'chargebackContact'
      mode: 'edit'
      id: string
      index: number
      defaultValues: ChargebackContact
    }
  | { open: true; role: 'signer'; mode: 'add' }
  | {
      open: true
      role: 'signer'
      mode: 'edit'
      id: string
      index: number
      defaultValues: Signer
    }

export type IndividualsErrors =
  FieldErrors<IndividualWithRelationsFormValues> & {
    individuals?: {
      mainContact?: { message?: string }
      chargebackContact?: { message?: string }
      signer?: { message?: string }
    }
  }

const refetchQueries = ['getMerchantApplication']

export const useIndividualsField = () => {
  const { t } = useTranslation()
  const { data: merchantData } = useGetMerchantApplicationQuery()
  const merchantId = merchantData?.merchantApplication?.merchantId ?? ''

  const {
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useFormContext<IndividualWithRelationsFormValues>()

  const [modal, setModal] = useState<ModalState>({ open: false })

  const [createIndividual, { loading: creating }] =
    useCreateIndividualWithRelationMutation({ refetchQueries })
  const [updateIndividual, { loading: updating }] = useUpdateIndividualMutation(
    { refetchQueries }
  )
  const [updateIndividualRelation, { loading: updatingRelation }] =
    useUpdateIndividualRelationMutation({ refetchQueries })
  const [removeIndividual] = useRemoveIndividualMutation({ refetchQueries })

  const isSaving = creating || updating || updatingRelation

  const allIndividuals: IndividualWithRelations[] = watch('individuals') ?? []

  const mainContact = findByRole(allIndividuals, rel => !!rel.isPrimaryContact)
  const chargebackContact = findByRole(
    allIndividuals,
    rel =>
      !!rel.isContact &&
      !!rel.contactTypes?.includes(IndividualContactType.Chargeback)
  )
  const signer = findByRole(allIndividuals, rel => !!rel.signsTaxDocuments)

  const configs = {
    mainContact: getMainContactModalConfig(t),
    chargebackContact: getChargebackContactModalConfig(t),
    signer: getSignerModalConfig(t)
  }

  const updateFormIndividual = (
    index: number,
    individualUpdate: object,
    relationUpdate: object,
    relationPredicate: (rel: IndividualRelationObject) => boolean
  ) => {
    const current = getValues('individuals') as IndividualWithRelations[]
    const updated = current.map((ind, i) =>
      i === index
        ? {
            ...ind,
            individual: { ...ind.individual, ...individualUpdate },
            parentRelations: ind.parentRelations?.map(rel =>
              relationPredicate(rel) ? { ...rel, ...relationUpdate } : rel
            )
          }
        : ind
    )
    setValue('individuals', updated, { shouldDirty: true })
  }

  const onSaveMainContact = async (data: MainContact) => {
    if (!modal.open || modal.mode !== 'edit') return

    await Promise.all([
      updateIndividual({
        variables: { input: { id: modal.id, ...data.individual } }
      }),
      updateIndividualRelation({
        variables: {
          input: {
            individualId: modal.id,
            relatedEntityId: merchantId,
            title: data.relation.title,
            isPrimaryContact: true
          }
        }
      })
    ])
    updateFormIndividual(
      modal.index,
      data.individual,
      { title: data.relation.title },
      rel => !!rel.isPrimaryContact
    )

    setModal({ open: false })
  }

  const onSaveChargebackContact = async (data: ChargebackContact) => {
    const current = getValues('individuals') as IndividualWithRelations[]

    if (modal.open && modal.mode === 'edit') {
      await Promise.all([
        updateIndividual({
          variables: { input: { id: modal.id, ...data.individual } }
        }),
        updateIndividualRelation({
          variables: {
            input: {
              individualId: modal.id,
              relatedEntityId: merchantId,
              title: data.relation.title,
              isContact: true,
              contactTypes: [IndividualContactType.Chargeback]
            }
          }
        })
      ])
      updateFormIndividual(
        modal.index,
        data.individual,
        { title: data.relation.title },
        rel =>
          !!rel.isContact &&
          !!rel.contactTypes?.includes(IndividualContactType.Chargeback)
      )
    } else {
      const result = await createIndividual({
        variables: {
          input: {
            individual: { type: IndividualType.Individual, ...data.individual },
            individualRelation: {
              title: data.relation.title,
              isContact: true,
              contactTypes: [IndividualContactType.Chargeback]
            }
          }
        }
      })
      const newId = result.data?.createIndividualWithRelation?.id ?? ''
      setValue(
        'individuals',
        [
          ...current,
          {
            individual: {
              id: newId,
              type: IndividualType.Individual,
              ...data.individual
            },
            parentRelations: [
              {
                individualId: newId,
                title: data.relation.title,
                isContact: true,
                contactTypes: [IndividualContactType.Chargeback]
              }
            ]
          }
        ],
        { shouldDirty: true }
      )
    }

    setModal({ open: false })
  }

  const onSaveSigner = async (data: Signer) => {
    const current = getValues('individuals') as IndividualWithRelations[]
    const individualInput = mapSignerToInput(data)

    if (modal.open && modal.mode === 'edit') {
      await Promise.all([
        updateIndividual({
          variables: { input: { id: modal.id, ...individualInput } }
        }),
        updateIndividualRelation({
          variables: {
            input: {
              individualId: modal.id,
              relatedEntityId: merchantId,
              title: data.relation.title,
              signsTaxDocuments: true
            }
          }
        })
      ])
      updateFormIndividual(
        modal.index,
        individualInput,
        { title: data.relation.title },
        rel => !!rel.signsTaxDocuments
      )
    } else {
      const result = await createIndividual({
        variables: {
          input: {
            individual: {
              type: IndividualType.Individual,
              ...individualInput
            },
            individualRelation: {
              title: data.relation.title,
              signsTaxDocuments: true
            }
          }
        }
      })
      const newId = result.data?.createIndividualWithRelation?.id ?? ''
      setValue(
        'individuals',
        [
          ...current,
          {
            individual: {
              id: newId,
              type: IndividualType.Individual,
              ...individualInput
            },
            parentRelations: [
              {
                individualId: newId,
                title: data.relation.title,
                signsTaxDocuments: true
              }
            ]
          }
        ],
        { shouldDirty: true }
      )
    }

    setModal({ open: false })
  }

  const onRemoveByRole = async (
    role: 'mainContact' | 'chargebackContact' | 'signer'
  ) => {
    const roleMatchMap = { mainContact, chargebackContact, signer }
    const match = roleMatchMap[role]
    if (!match) return

    const roleClearMap = {
      mainContact: { isPrimaryContact: false },
      chargebackContact: {
        isContact: false,
        contactTypes: [] as IndividualContactType[]
      },
      signer: { signsTaxDocuments: false }
    }

    const rolePredicateMap = {
      mainContact: (rel: IndividualRelationObject) => !!rel.isPrimaryContact,
      chargebackContact: (rel: IndividualRelationObject) =>
        !!rel.isContact &&
        !!rel.contactTypes?.includes(IndividualContactType.Chargeback),
      signer: (rel: IndividualRelationObject) => !!rel.signsTaxDocuments
    }

    if (match.relation?.isUbo) {
      await updateIndividualRelation({
        variables: {
          input: {
            individualId: match.individual.id,
            relatedEntityId: merchantId,
            ...roleClearMap[role]
          }
        }
      })
      updateFormIndividual(
        match.index,
        {},
        roleClearMap[role],
        rolePredicateMap[role]
      )
    } else {
      await removeIndividual({
        variables: { input: { id: match.individual.id } }
      })
      const current = getValues('individuals') as IndividualWithRelations[]
      setValue(
        'individuals',
        current.filter((_, i) => i !== match.index),
        { shouldDirty: true }
      )
    }
  }

  const openEditModal = (
    role: 'mainContact' | 'chargebackContact' | 'signer'
  ) => {
    if (role === 'mainContact' && mainContact) {
      setModal({
        open: true,
        role: 'mainContact',
        mode: 'edit',
        id: mainContact.individual.id,
        index: mainContact.index,
        defaultValues: mapToFormIndividual(mainContact)
      })
    } else if (role === 'chargebackContact' && chargebackContact) {
      setModal({
        open: true,
        role: 'chargebackContact',
        mode: 'edit',
        id: chargebackContact.individual.id,
        index: chargebackContact.index,
        defaultValues: mapToFormIndividual(chargebackContact)
      })
    } else if (role === 'signer' && signer) {
      setModal({
        open: true,
        role: 'signer',
        mode: 'edit',
        id: signer.individual.id,
        index: signer.index,
        defaultValues: mapToFormIndividual(signer)
      })
    }
  }

  const onConfirmMap = {
    mainContact: onSaveMainContact,
    chargebackContact: onSaveChargebackContact,
    signer: onSaveSigner
  }

  const modalConfig = modal.open
    ? {
        title:
          modal.mode === 'edit'
            ? t(`individuals.${modal.role}.modal.editTitle`)
            : t(`individuals.${modal.role}.modal.createTitle`),
        subTitle: t(`individuals.${modal.role}.modal.subTitle`, {
          defaultValue: ''
        }),
        submitLabel:
          modal.mode === 'edit'
            ? t('actions.save')
            : t(`individuals.${modal.role}.addButton`),
        config: configs[modal.role] as unknown as IFormConfig<FieldValues>,
        defaultValues:
          'defaultValues' in modal ? modal.defaultValues : undefined,
        onConfirm: onConfirmMap[modal.role] as unknown as (
          data: FieldValues
        ) => void
      }
    : null

  return {
    modal,
    setModal,
    modalConfig,
    mainContact,
    chargebackContact,
    signer,
    openEditModal,
    onRemoveByRole,
    isSaving,
    individualsErrors: errors as IndividualsErrors
  }
}
